// Self-hosted REST API client (replaces Supabase).
// Talks to the Express + Prisma backend. All backend responses are shaped
// { success: boolean, data?: any, error?: string }. Helper methods below unwrap
// that and return { data, error } so call-sites read like the old Supabase calls.

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/$/, '');

// Native (Capacitor) app detection — Capacitor injects window.Capacitor.
const IS_NATIVE = !!(window as any).Capacitor?.isNativePlatform?.();
const REFRESH_KEY = 'fc_refresh_token';

// Access token is held in MEMORY only (VULN-06). On the WEB the refresh token is
// an HttpOnly cookie the browser sends automatically. In the NATIVE app, cookies
// don't work in the webview, so the refresh token is kept in native secure
// storage (Capacitor Preferences) and sent in the request body with 'x-app: 1'.
let accessToken: string | null = null;
let refreshTokenMem: string | null = null;

async function prefs() { return (await import('@capacitor/preferences')).Preferences; }
async function saveNativeRefresh(token: string) {
  refreshTokenMem = token;
  if (IS_NATIVE) { try { await (await prefs()).set({ key: REFRESH_KEY, value: token }); } catch { /* ignore */ } }
}

// Hydrate the stored refresh token at startup (native only). AuthContext awaits
// this before restoring the session.
export const nativeReady: Promise<void> = IS_NATIVE
  ? prefs().then((p) => p.get({ key: REFRESH_KEY })).then((r) => { refreshTokenMem = r.value ?? null; }).catch(() => {})
  : Promise.resolve();

export const tokenStore = {
  get access(): string | null { return accessToken; },
  set(access: string | null) { accessToken = access; },
  clear() {
    accessToken = null;
    refreshTokenMem = null;
    if (IS_NATIVE) { prefs().then((p) => p.remove({ key: REFRESH_KEY })).catch(() => {}); }
  },
};

// Save a fresh refresh token (native only) after login/refresh.
export async function persistNativeRefresh(token: string | undefined) {
  if (IS_NATIVE && token) await saveNativeRefresh(token);
}

// AuthContext registers a handler so a hard 401 (failed refresh) forces logout.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export type Result<T = any> = { data: T | null; error: Error | null };

function buildQuery(params?: Record<string, any>): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.append(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function tryRefresh(): Promise<boolean> {
  try {
    // Web: refresh token in the HttpOnly cookie. Native: in the body + x-app.
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(IS_NATIVE ? { 'x-app': '1' } : {}) },
      credentials: 'include',
      body: (IS_NATIVE && refreshTokenMem) ? JSON.stringify({ refreshToken: refreshTokenMem }) : undefined,
    });
    const json = await res.json().catch(() => ({} as any));
    if (res.ok && json?.success && json?.data?.accessToken) {
      tokenStore.set(json.data.accessToken);
      if (IS_NATIVE && json.data.refreshToken) await saveNativeRefresh(json.data.refreshToken);
      return true;
    }
  } catch {
    // fall through
  }
  return false;
}

// Core request. Throws Error on failure (non-2xx or { success:false }).
async function request<T = any>(
  method: string,
  path: string,
  body?: any,
  _retry = true
): Promise<T> {
  const access = tokenStore.access;
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(IS_NATIVE ? { 'x-app': '1' } : {}),
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
    },
    credentials: 'include', // send/receive the HttpOnly refresh cookie (web)
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && _retry) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(method, path, body, false);
    tokenStore.clear();
    if (onUnauthorized) onUnauthorized();
    throw new Error('Session expired. Please sign in again.');
  }

  let json: any = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  if (!res.ok || (json && json.success === false)) {
    throw new Error((json && json.error) || `Request failed (${res.status})`);
  }
  // Endpoints that only return { success: true } yield undefined data.
  return (json ? json.data : undefined) as T;
}

// Wrap a request so it returns { data, error } instead of throwing.
async function wrap<T = any>(fn: () => Promise<T>): Promise<Result<T>> {
  try {
    return { data: await fn(), error: null };
  } catch (e: any) {
    return { data: null, error: e instanceof Error ? e : new Error(String(e)) };
  }
}

export interface LoginResult {
  accessToken: string;
  refreshToken?: string; // present only for the native app
  user: { id: string; email: string; role: string; profileId: string; fullName: string | null };
}

export const api = {
  auth: {
    login: (email: string, password: string) =>
      wrap<LoginResult>(async () => {
        const data = await request<LoginResult>('POST', '/auth/login', { email, password });
        await persistNativeRefresh(data?.refreshToken);
        return data;
      }),
    logout: () => wrap(async () => {
      const body = IS_NATIVE && refreshTokenMem ? { refreshToken: refreshTokenMem } : undefined;
      await request('POST', '/auth/logout', body);
    }),
    me: () => wrap<{ role: string; profile: any }>(() => request('GET', '/auth/me')),
    createUser: (email: string, password: string) =>
      wrap<{ user: { id: string; email: string } }>(() =>
        request('POST', '/auth/admin/create-user', { email, password })
      ),
    // Get-or-create an auth user by email (lets one email hold multiple roles).
    ensureUser: (email: string, password?: string) =>
      wrap<{ user: { id: string; email: string }; created: boolean }>(() =>
        request('POST', '/auth/admin/ensure-user', { email, password })
      ),
    deleteUser: (userId: string) =>
      wrap(() => request('DELETE', `/auth/admin/delete-user/${userId}`)),
    forgotPassword: (email: string) =>
      wrap(() => request('POST', '/auth/forgot-password', { email })),
    resetPassword: (token: string, password: string) =>
      wrap(() => request('POST', '/auth/reset-password', { token, password })),
  },

  employees: {
    list: (params?: { category?: string; status?: string; search?: string }) =>
      wrap<any[]>(() => request('GET', `/employees${buildQuery(params)}`)),
    getById: (id: string) => wrap<any>(() => request('GET', `/employees/${id}`)),
    getByQr: (qr: string) => wrap<any>(() => request('GET', `/employees/by-qr/${encodeURIComponent(qr)}`)),
    countStats: (params?: { status?: string; category?: string }) =>
      wrap<{ count: number }>(() => request('GET', `/employees/stats/count${buildQuery(params)}`)),
    create: (body: any) => wrap<any>(() => request('POST', '/employees', body)),
    update: (id: string, body: any) => wrap<any>(() => request('PATCH', `/employees/${id}`, body)),
    addBalance: (id: string, body: { amount: number; notes?: string }) =>
      wrap<{ balance_after: number }>(() => request('POST', `/employees/${id}/add-balance`, body)),
    setPin: (id: string, body: { pin?: string; pin_hash?: string; is_temporary?: boolean; must_change_on_login?: boolean }) =>
      wrap<any>(() => request('POST', `/employees/${id}/pin`, body)),
    setPassword: (id: string, password: string) =>
      wrap(() => request('POST', `/employees/${id}/set-password`, { password })),
  },

  vendors: {
    list: (params?: { status?: string; search?: string }) =>
      wrap<any[]>(() => request('GET', `/vendors${buildQuery(params)}`)),
    active: () => wrap<any[]>(() => request('GET', '/vendors/active')),
    getById: (id: string) => wrap<any>(() => request('GET', `/vendors/${id}`)),
    byEmail: (email: string) => wrap<any>(() => request('GET', `/vendors/by-email/${encodeURIComponent(email)}`)),
    countStats: (params?: { status?: string }) =>
      wrap<{ count: number }>(() => request('GET', `/vendors/stats/count${buildQuery(params)}`)),
    create: (body: any) => wrap<any>(() => request('POST', '/vendors', body)),
    update: (id: string, body: any) => wrap<any>(() => request('PATCH', `/vendors/${id}`, body)),
    setPin: (id: string, body: { pin?: string; pin_hash?: string; is_temporary?: boolean; must_change_on_login?: boolean }) =>
      wrap<any>(() => request('POST', `/vendors/${id}/pin`, body)),
    setPassword: (id: string, password: string) =>
      wrap(() => request('POST', `/vendors/${id}/set-password`, { password })),
  },

  counters: {
    list: (params?: { vendor_id?: string }) =>
      wrap<any[]>(() => request('GET', `/counters${buildQuery(params)}`)),
    getById: (id: string) => wrap<any>(() => request('GET', `/counters/${id}`)),
    create: (body: any) => wrap<any>(() => request('POST', '/counters', body)),
    update: (id: string, body: any) => wrap<any>(() => request('PATCH', `/counters/${id}`, body)),
    remove: (id: string) => wrap(() => request('DELETE', `/counters/${id}`)),
    batchUpdate: (body: { vendor_id: string; updates: any }) =>
      wrap(() => request('POST', '/counters/batch-update', body)),
  },

  orders: {
    list: (params?: { vendor_id?: string; employee_id?: string; status?: string; limit?: number }) =>
      wrap<any[]>(() => request('GET', `/orders${buildQuery(params)}`)),
    countStats: (params?: { vendor_id?: string; status?: string }) =>
      wrap<{ count: number }>(() => request('GET', `/orders/stats/count${buildQuery(params)}`)),
    vendorStats: (vendorId: string, params?: { since?: string; month_start?: string }) =>
      wrap<{ todayOrders: any[]; monthOrders: any[]; recentOrders: any[] }>(() =>
        request('GET', `/orders/stats/vendor/${vendorId}${buildQuery(params)}`)
      ),
    getById: (id: string) => wrap<any>(() => request('GET', `/orders/${id}`)),
    itemsBatch: (order_ids: string[]) =>
      wrap<any[]>(() => request('POST', '/orders/items/batch', { order_ids })),
    update: (id: string, body: any) => wrap<any>(() => request('PATCH', `/orders/${id}`, body)),
  },

  menu: {
    list: (params?: { vendor_id?: string; available_only?: boolean }) =>
      wrap<any[]>(() => request('GET', `/menu-items${buildQuery(params)}`)),
    getById: (id: string) => wrap<any>(() => request('GET', `/menu-items/${id}`)),
    create: (body: any) => wrap<any>(() => request('POST', '/menu-items', body)),
    update: (id: string, body: any) => wrap<any>(() => request('PATCH', `/menu-items/${id}`, body)),
    remove: (id: string) => wrap(() => request('DELETE', `/menu-items/${id}`)),
  },

  transactions: {
    list: (params?: {
      employee_id?: string; vendor_id?: string; counter_id?: string;
      transaction_type?: string; since?: string; until?: string; limit?: number;
    }) => wrap<any[]>(() => request('GET', `/transactions${buildQuery(params)}`)),
    summary: (params?: { since?: string; vendor_id?: string }) =>
      wrap<{ total_amount: number; count: number }>(() =>
        request('GET', `/transactions/stats/summary${buildQuery(params)}`)
      ),
    create: (body: any) => wrap<any>(() => request('POST', '/transactions', body)),
  },

  admin: {
    users: () => wrap<any[]>(() => request('GET', '/admin/users')),
    createUser: (body: any) => wrap<any>(() => request('POST', '/admin/users', body)),
    updateUser: (id: string, body: any) => wrap<any>(() => request('PATCH', `/admin/users/${id}`, body)),
    deleteUser: (id: string) => wrap(() => request('DELETE', `/admin/users/${id}`)),
    dashboard: () => wrap<any>(() => request('GET', '/admin/dashboard')),
    runDailySummary: () => wrap<{ sent: number; vendorsWithTxns: number; date: string; emailConfigured: boolean }>(
      () => request('POST', '/admin/run-daily-summary')),
    unlockUser: (email: string) => wrap(() => request('POST', '/admin/unlock-user', { email })),
    bulkCreateEmployees: (employees: any[]) =>
      wrap<{ created: number; skipped: number; errors: string[] }>(
        () => request('POST', '/employees/bulk', { employees })),
  },

  categories: {
    list: () => wrap<any[]>(() => request('GET', '/categories')),
    create: (body: any) => wrap<any>(() => request('POST', '/categories', body)),
    update: (id: string, body: any) => wrap<any>(() => request('PATCH', `/categories/${id}`, body)),
    remove: (id: string) => wrap(() => request('DELETE', `/categories/${id}`)),
  },

  settings: {
    list: () => wrap<any[]>(() => request('GET', '/settings')),
    get: (key: string) => wrap<any>(() => request('GET', `/settings/${encodeURIComponent(key)}`)),
    batch: (settings: Record<string, any>) =>
      wrap(() => request('PUT', '/settings/batch', { settings })),
    set: (key: string, value: any) =>
      wrap(() => request('PATCH', `/settings/${encodeURIComponent(key)}`, { value })),
  },

  rpc: {
    processTransaction: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/process-transaction', args)),
    processCounterTransaction: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/process-counter-transaction', args)),
    verifyEmployeePin: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/verify-employee-pin', args)),
    verifyCounterPin: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/verify-counter-pin', args)),
    placeFoodOrderWithCart: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/place-food-order-with-cart', args)),
    vendorAcceptOrder: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/vendor-accept-order', args)),
    vendorRejectOrder: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/vendor-reject-order', args)),
    completeOrderWithBalanceUpdate: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/complete-order-with-balance-update', args)),
    cancelOrderItem: (args: any) =>
      wrap<any>(() => request('POST', '/rpc/cancel-order-item', args)),
  },

  poll: {
    employeeBalance: (id: string) =>
      wrap<{ available_balance: number; current_balance: number }>(() =>
        request('GET', `/poll/employee-balance/${id}`)
      ),
    orders: (params?: { vendor_id?: string; employee_id?: string; since?: string }) =>
      wrap<any[]>(() => request('GET', `/poll/orders${buildQuery(params)}`)),
    transactions: (params?: { employee_id?: string; vendor_id?: string; since?: string }) =>
      wrap<any[]>(() => request('GET', `/poll/transactions${buildQuery(params)}`)),
  },
};

export default api;
